import type {
	Product,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";

export interface GtmItem {
	item_id?: string | number;
	item_sku?: string;
	item_norce_id?: number;
	item_name?: string;
	item_brand?: string;
	item_category?: string | number;
	image_url?: string;
	product_url?: string;
	price: number;
	quantity: number;
}

export type GtmTrackableItem =
	| Product
	| ProductItem
	| BasketItem
	| FilteredProductItem
	| Partial<FilteredProductItem>
	| Partial<BasketItem>;

export interface CreateGtmItemContext {
	// CDN base URL used to build absolute image_url from ImageKey.
	cdnBaseUrl?: string;
	// Site origin used to build absolute product_url from UniqueName.
	origin?: string;
}

const pickImageKey = (item: GtmTrackableItem): string | undefined => {
	// Prefer the canonical CDN GUID keys (VariantImageKey for variants, then
	// ImageKey). The legacy Image / ThumbnailImage / LargeImage fields can
	// contain raw filenames that don't resolve against the Norce CDN
	// (e.g. "103white-1.jpg"), so they're only used as a last-resort fallback.
	// Absolute http(s) URLs in any of these fields are passed through
	// untouched by buildImageUrl, so the reordering doesn't change behavior
	// for templates that already store full URLs there.
	if (
		"VariantImageKey" in item &&
		typeof (item as { VariantImageKey?: string | null }).VariantImageKey ===
			"string" &&
		(item as { VariantImageKey: string }).VariantImageKey
	)
		return (item as { VariantImageKey: string }).VariantImageKey;
	if ("ImageKey" in item && typeof item.ImageKey === "string" && item.ImageKey)
		return item.ImageKey;
	if ("Image" in item && typeof item.Image === "string" && item.Image)
		return item.Image;
	if (
		"ThumbnailImage" in item &&
		typeof item.ThumbnailImage === "string" &&
		item.ThumbnailImage
	)
		return item.ThumbnailImage;
	if (
		"LargeImage" in item &&
		typeof item.LargeImage === "string" &&
		item.LargeImage
	)
		return item.LargeImage;
	return undefined;
};

const pickSku = (item: GtmTrackableItem): string | undefined => {
	if (
		"PartNo" in item &&
		typeof (item as { PartNo?: string }).PartNo === "string"
	) {
		return (item as { PartNo: string }).PartNo;
	}
	if ("Code" in item && typeof (item as { Code?: string }).Code === "string") {
		return (item as { Code: string }).Code;
	}
	return undefined;
};

const pickSlug = (item: GtmTrackableItem): string | undefined => {
	if (
		"UniqueName" in item &&
		typeof (item as { UniqueName?: string }).UniqueName === "string"
	) {
		return (item as { UniqueName: string }).UniqueName;
	}
	if (
		"VariantUniqueName" in item &&
		typeof (item as { VariantUniqueName?: string }).VariantUniqueName ===
			"string"
	) {
		return (item as { VariantUniqueName: string }).VariantUniqueName;
	}
	return undefined;
};

const buildImageUrl = (
	key: string | undefined,
	cdnBaseUrl: string | undefined
): string | undefined => {
	if (!key) return undefined;
	if (/^https?:\/\//i.test(key)) return key;
	if (!cdnBaseUrl) return undefined;
	return `${cdnBaseUrl.replace(/\/$/, "")}/${key.replace(/^\//, "")}`;
};

const buildProductUrl = (
	slug: string | undefined,
	origin: string | undefined
): string | undefined => {
	if (!slug) return undefined;
	if (!origin) return `/${slug}`;
	return `${origin}/${slug}`;
};

export const createGtmItem = (
	item: GtmTrackableItem,
	category?: string | number,
	defaultQuantity: number = 1,
	context: CreateGtmItemContext = {}
): GtmItem | null => {
	if (
		!("Id" in item) ||
		item.Id == null ||
		!("Name" in item) ||
		item.Name == null
	) {
		console.warn("Item is missing Id or Name", item);
		return null;
	}

	const price =
		"PriceDisplayIncVat" in item && item.PriceDisplayIncVat != null
			? item.PriceDisplayIncVat
			: "PriceIncVat" in item && item.PriceIncVat != null
				? item.PriceIncVat
				: 0;

	const brand =
		"ManufacturerName" in item && item.ManufacturerName != null
			? item.ManufacturerName
			: "Manufacturer" in item && item.Manufacturer?.Name != null
				? item.Manufacturer.Name
				: undefined;

	// Only ever a human-readable name. The numeric CategoryId / CategoryIdSeed
	// fallbacks used to leak ids into reports ("35", "57,42,55") — an absent
	// item_category is better than an unreadable one.
	const itemCategory =
		category ??
		("CategoryName" in item && item.CategoryName ? item.CategoryName : null) ??
		("Type" in item && item.Type === 3 ? "Freight" : undefined);

	const sku = pickSku(item);
	const imageUrl = buildImageUrl(pickImageKey(item), context.cdnBaseUrl);
	const productUrl = buildProductUrl(pickSlug(item), context.origin);

	return {
		// PartNo, not the Norce Id: the old Jetshop site keyed GA4 on the SKU, so
		// this preserves item reporting across the migration. It is also stable
		// between catalog and basket items, where the Norce Id is not.
		// `||`, not `??`: BasketItem.PartNo is typed non-optional, so an empty
		// string would otherwise become the item_id.
		item_id: sku || item.Id,
		...(sku ? { item_sku: sku } : {}),
		...(sku ? { item_norce_id: item.Id } : {}),
		item_name: item.Name,
		...(brand ? { item_brand: brand } : {}),
		...(itemCategory ? { item_category: itemCategory } : {}),
		...(imageUrl ? { image_url: imageUrl } : {}),
		...(productUrl ? { product_url: productUrl } : {}),
		price,
		quantity:
			"Quantity" in item && item.Quantity != null
				? item.Quantity
				: defaultQuantity
	};
};
